<!-- ABOUTME: Multi-language email form component for broadcast emails and templates -->
<!-- ABOUTME: Handles 4 languages (EN, SK, CS, HU) with manual save and Trix editor integration -->
<template>
  <div class="bg-white">
    <!-- Template Selector (only for broadcast emails) -->
    <div v-if="showTemplateSelector" class="mb-3">
      <label class="">
        Load from Template (Optional)
      </label>
      <select
        v-model="selectedTemplateId"
        @change="loadTemplate"
        class="">
        <option value="">Select a template...</option>
        <option
          v-for="template in templates"
          :key="template.id"
          :value="template.id">
          {{ template.template_key }}
        </option>
      </select>
    </div>

    <!-- Language Tabs -->
    <div class="mb-3 border-b-2 border-gray-200">
      <div class="flex gap-1 flex-wrap">
        <button
          v-for="lang in languages"
          :key="lang.code"
          type="button"
          @click="switchLanguage(lang.code)"
          class="px-4 py-2 border-none bg-transparent cursor-pointer font-medium text-sm transition-all hover:bg-gray-50"
          :class="[
            currentLocale === lang.code
              ? 'border-b-[3px] border-b-primary text-primary'
              : 'border-b-[3px] border-b-transparent text-gray-500'
          ]">
          {{ lang.name }}
          <span v-if="variantStatus[lang.code]" class="ml-1.5 text-green-500 text-xs">
            {{ variantStatus[lang.code] }}
          </span>
        </button>
      </div>
    </div>

    <!-- Language Panel - Single panel that changes content based on currentLocale -->
    <div class="animate-fade-in">
      <div class="mb-5">
        <label class="block text-sm font-semibold text-gray-700 mb-2">
          Subject ({{ currentLanguageName }})
        </label>
        <input
          type="text"
          v-model="variants[currentLocale].subject"
          @input="updateVariantStatus(currentLocale)"
          :placeholder="`Email subject in ${currentLanguageName}`"
          class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 transition-colors focus:border-primary focus:outline-none focus:ring-primary">
      </div>

      <div class="mb-5">
        <label class="block text-sm font-semibold text-gray-700 mb-2">
          Email Body ({{ currentLanguageName }})
        </label>
        <trix-editor
          ref="trixEditor"
          input="variant_body_content"
          class="min-h-[300px] border border-gray-300 rounded-md p-3">
        </trix-editor>
        <input type="hidden" id="variant_body_content">

        <div class="mt-4 p-3 bg-gray-50 rounded-md border border-gray-200">
          <div class="text-xs text-gray-600 leading-relaxed">
            <strong class="text-gray-700">Available variables:</strong><br>
            <span v-html="`{{first_name}}, {{last_name}}, {{email}}`"></span><br>
            <span v-html="`{{network_url}}, {{projects_url}}, {{login_url}}`"></span><br>
            <br>
            <strong class="text-gray-700">Buttons (with text + URL):</strong><br>
            <code class="bg-white px-1.5 py-0.5 rounded text-[11px]" v-html="`{{button:primary:URL:Button Text}}`"></code><br>
            <code class="bg-white px-1.5 py-0.5 rounded text-[11px]" v-html="`{{button:secondary:URL:Button Text}}`"></code><br>
            <code class="bg-white px-1.5 py-0.5 rounded text-[11px]" v-html="`{{button:success:URL:Button Text}}`"></code><br>
            <code class="bg-white px-1.5 py-0.5 rounded text-[11px]" v-html="`{{button:danger:URL:Button Text}}`"></code><br>
            <br>
            <strong class="text-gray-700">Links (with text + URL):</strong><br>
            <code class="bg-white px-1.5 py-0.5 rounded text-[11px]" v-html="`{{link:URL:Link Text}}`"></code><br>
            <em class="text-gray-500">Example: <code class="bg-white px-1.5 py-0.5 rounded text-[11px]" v-html="`{{link:{{network_url}}:View Network}}`"></code></em>
          </div>
        </div>
      </div>
    </div>

    <!-- Validation Message -->
    <div v-if="validationError" class="mt-4 px-4 py-3 bg-amber-50 border-l-4 border-amber-500 rounded-md">
      <p class="m-0 text-sm text-amber-900">
        {{ validationError }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick, computed } from 'vue'
import axios from 'axios'
import { Send } from 'lucide-vue-next'

// Props
const props = defineProps({
  emailContentId: {
    type: [String, Number],
    default: null
  },
  initialVariants: {
    type: Object,
    default: () => ({})
  },
  templates: {
    type: Array,
    default: () => []
  },
  showTemplateSelector: {
    type: Boolean,
    default: false
  },
  saveUrl: {
    type: String,
    required: true
  },
  emailType: {
    type: String,
    default: 'broadcast' // 'broadcast' or 'template'
  }
})

// Emits
const emit = defineEmits(['saved', 'error'])

// State
const languages = [
  { code: 'en', name: 'English' },
  { code: 'sk', name: 'Slovak' },
  { code: 'cs', name: 'Czech' },
  { code: 'hu', name: 'Hungarian' }
]

const currentLocale = ref('en')
const variants = reactive({
  en: { id: null, subject: '', body: '' },
  sk: { id: null, subject: '', body: '' },
  cs: { id: null, subject: '', body: '' },
  hu: { id: null, subject: '', body: '' }
})
const variantStatus = reactive({
  en: '',
  sk: '',
  cs: '',
  hu: ''
})
const saveMessage = ref('')
const saveMessageClass = ref('')
const validationError = ref('')
const selectedTemplateId = ref('')
const trixEditor = ref(null)
const emailContentIdStore = ref(props.emailContentId)
const previewMessage = ref('')
const previewMessageClass = ref('')
const previewLoading = ref(false)

// Computed property for current language name
const currentLanguageName = computed(() => {
  const lang = languages.find(l => l.code === currentLocale.value)
  return lang ? lang.name : 'English'
})

// Check if current variant has enough content to send preview
const canSendPreview = computed(() => {
  const subject = variants[currentLocale.value].subject?.trim()
  const body = variants[currentLocale.value].body?.trim()
  return subject && body
})

// Initialize variants from props
onMounted(async () => {
  console.log('Component mounted, initialVariants:', props.initialVariants)

  if (props.initialVariants && typeof props.initialVariants === 'object') {
    // Load all variant data into the variants object
    Object.keys(props.initialVariants).forEach(locale => {
      if (variants[locale]) {
        const variantData = props.initialVariants[locale]
        variants[locale].id = variantData.id || null
        variants[locale].subject = variantData.subject || ''
        variants[locale].body = variantData.body || ''
        console.log(`Loaded ${locale} variant:`, {
          id: variants[locale].id,
          subject: variants[locale].subject,
          bodyLength: variants[locale].body.length
        })
      }
    })

    // Wait for single Trix editor to be mounted
    await nextTick()
    await nextTick()

    // Load content into the editor for current locale (English by default)
    if (trixEditor.value && trixEditor.value.editor) {
      const contentToLoad = variants[currentLocale.value].body
      if (contentToLoad) {
        console.log(`Loading initial content for ${currentLocale.value}:`, contentToLoad.substring(0, 100))
        trixEditor.value.editor.loadHTML(contentToLoad)
      }
    } else {
      console.warn('Trix editor not ready on mount, retrying...')
      setTimeout(() => {
        if (trixEditor.value && trixEditor.value.editor && variants[currentLocale.value].body) {
          trixEditor.value.editor.loadHTML(variants[currentLocale.value].body)
        }
      }, 200)
    }

    // Update all variant status indicators
    updateAllVariantStatuses()
  }
})

// Get CSRF token
function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.content : ''
}

// Update single variant status indicator
function updateVariantStatus(locale) {
  const hasContent = variants[locale].subject && variants[locale].subject.trim()
  variantStatus[locale] = hasContent ? '•' : ''
}

// Update all variant status indicators
function updateAllVariantStatuses() {
  languages.forEach(lang => {
    updateVariantStatus(lang.code)
  })
}

// Clean Trix HTML to remove extra line breaks and empty divs
function cleanTrixHTML(html) {
  if (!html) return ''

  return html
    // Remove empty div wrappers: <div><br></div> → <br>
    .replace(/<div>\s*<br>\s*<\/div>/gi, '<br>')
    // Remove standalone empty divs
    .replace(/<div>\s*<\/div>/gi, '')
    // Convert multiple consecutive <br> tags (3+) to just 2
    .replace(/(<br\s*\/?>)\s*<br\s*\/?>\s*<br\s*\/?>/gi, '$1<br>')
    // Trim extra whitespace
    .trim()
}

// Switch language tab
function switchLanguage(locale) {
  if (locale === currentLocale.value) return

  // Save current editor content to variants object before switching
  if (trixEditor.value && trixEditor.value.editor) {
    const htmlContent = trixEditor.value.editor.element.value
    variants[currentLocale.value].body = cleanTrixHTML(htmlContent)
    console.log(`Saved ${currentLocale.value} content (${htmlContent.length} chars)`)
  }

  // Switch to new locale
  const oldLocale = currentLocale.value
  currentLocale.value = locale
  console.log(`Switching from ${oldLocale} to ${locale}`)

  // Load content for the new locale into the SAME editor
  if (trixEditor.value && trixEditor.value.editor) {
    const contentToLoad = variants[locale].body || ''
    console.log(`Loading ${locale} content (${contentToLoad.length} chars)`)
    trixEditor.value.editor.loadHTML(contentToLoad)
  }
}

// Save current variant (called by parent form)
async function saveCurrentVariant() {
  // Sync current editor content to variants object before saving
  if (trixEditor.value && trixEditor.value.editor) {
    variants[currentLocale.value].body = cleanTrixHTML(trixEditor.value.editor.element.value)
  }

  const locale = currentLocale.value
  const subject = variants[locale].subject
  const body = variants[locale].body

  // Don't save if current variant is empty
  if (!subject || !subject.trim()) {
    return
  }

  saveMessage.value = 'Saving...'
  saveMessageClass.value = 'save-pending'
  validationError.value = ''

  try {
    const url = emailContentIdStore.value
      ? `${props.saveUrl}/${emailContentIdStore.value}/save_variant`
      : `${props.saveUrl}/save_variant`

    const response = await axios.post(url, {
      id: emailContentIdStore.value || 'undefined',
      locale: locale,
      subject: subject,
      body: body,
      // Add template-specific fields if needed
      ...(props.emailType === 'template' && {
        template_key: document.getElementById('email_content_template_key')?.value,
        title: document.getElementById('email_content_title')?.value
      }),
      // Add broadcast-specific fields if needed
      ...(props.emailType === 'broadcast' && {
        title: document.getElementById('email_content_title')?.value
      })
    }, {
      headers: {
        'X-CSRF-Token': getCsrfToken(),
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })

    if (response.data.success) {
      saveMessage.value = `✓ ${response.data.message}`
      saveMessageClass.value = 'save-success'

      // Store email content ID if this was first save
      if (!emailContentIdStore.value && response.data.email_content_id) {
        emailContentIdStore.value = response.data.email_content_id
        emit('saved', response.data.email_content_id)
      }

      // Update all variant statuses if provided
      if (response.data.all_variants) {
        response.data.all_variants.forEach(v => {
          if (v.has_content) {
            variantStatus[v.locale] = '•'
          }
        })
      }

      setTimeout(() => {
        saveMessage.value = ''
        saveMessageClass.value = ''
      }, 3000)
    } else {
      saveMessage.value = '✗ Save failed'
      saveMessageClass.value = 'save-error'
      const errors = response.data.errors || []
      validationError.value = 'Save failed: ' + errors.join(', ')
      console.error('Save failed:', errors)
    }
  } catch (error) {
    saveMessage.value = '✗ Error saving'
    saveMessageClass.value = 'save-error'
    const errorMsg = error.response?.data?.errors?.join(', ') || error.message
    validationError.value = 'Error saving: ' + errorMsg
    console.error('Save error:', error)
  }
}

// Load template
async function loadTemplate() {
  if (!selectedTemplateId.value) return

  if (!confirm('This will replace all current content with the template. Continue?')) {
    selectedTemplateId.value = ''
    return
  }

  try {
    const response = await axios.get(`/admin/email_templates/${selectedTemplateId.value}.json`, {
      headers: {
        'X-CSRF-Token': getCsrfToken()
      }
    })

    const template = response.data

    // Load variants
    if (template.variants && template.variants.length > 0) {
      // First, update all variants data
      template.variants.forEach(variant => {
        if (variants[variant.locale]) {
          variants[variant.locale].subject = variant.subject || ''
          variants[variant.locale].body = variant.body || ''
        }
      })

      // Update variant statuses
      updateAllVariantStatuses()

      // Load content into the editor for current locale
      await nextTick()
      if (trixEditor.value && trixEditor.value.editor && variants[currentLocale.value].body) {
        trixEditor.value.editor.loadHTML(variants[currentLocale.value].body)
      }

      saveMessage.value = '✓ Template loaded successfully'
      saveMessageClass.value = 'save-success'
      setTimeout(() => {
        saveMessage.value = ''
        saveMessageClass.value = ''
      }, 3000)
    } else {
      validationError.value = 'Template has no content variants'
    }
  } catch (error) {
    console.error('Failed to load template:', error)
    const errorMsg = error.response?.data?.error || error.message
    validationError.value = 'Failed to load template: ' + errorMsg
  }

  selectedTemplateId.value = ''
}

// Validate form (called from parent before submission)
function validate() {
  // First, sync current editor content to variants object
  if (trixEditor.value && trixEditor.value.editor) {
    variants[currentLocale.value].body = cleanTrixHTML(trixEditor.value.editor.element.value)
  }

  // Check if at least one variant has content
  let hasAnyVariant = false

  languages.forEach(lang => {
    const subject = variants[lang.code].subject?.trim()
    const body = variants[lang.code].body?.trim()

    if (subject && body) {
      hasAnyVariant = true
    }
  })

  if (!hasAnyVariant) {
    validationError.value = 'Please fill in at least one language variant (subject and body)'
    // Scroll to error message
    const errorElement = document.querySelector('.admin-alert-warning')
    if (errorElement) {
      errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    return false
  }

  validationError.value = ''
  return true
}

// Get all variants (called from parent before submission)
function getAllVariants() {
  // First, sync current editor content to variants object
  if (trixEditor.value && trixEditor.value.editor) {
    variants[currentLocale.value].body = cleanTrixHTML(trixEditor.value.editor.element.value)
  }

  // Return a copy of all variants with their current content
  return {
    en: { ...variants.en },
    sk: { ...variants.sk },
    cs: { ...variants.cs },
    hu: { ...variants.hu }
  }
}

// Send preview email with current variant content
async function sendPreview() {
  // Sync current editor content to variants object before sending
  if (trixEditor.value && trixEditor.value.editor) {
    variants[currentLocale.value].body = cleanTrixHTML(trixEditor.value.editor.element.value)
  }

  const locale = currentLocale.value
  const subject = variants[locale].subject
  const body = variants[locale].body

  // Don't send if current variant is empty
  if (!subject || !subject.trim() || !body || !body.trim()) {
    previewMessage.value = '⚠ Please fill in both subject and body to send preview'
    previewMessageClass.value = 'preview-error'
    setTimeout(() => {
      previewMessage.value = ''
      previewMessageClass.value = ''
    }, 3000)
    return
  }

  previewLoading.value = true
  previewMessage.value = 'Sending preview...'
  previewMessageClass.value = 'preview-pending'

  try {
    const url = emailContentIdStore.value
      ? `${props.saveUrl}/${emailContentIdStore.value}/send_preview`
      : `${props.saveUrl}/send_preview`

    const response = await axios.post(url, {
      locale: locale,
      subject: subject,
      body: body
    }, {
      headers: {
        'X-CSRF-Token': getCsrfToken(),
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })

    if (response.data.success) {
      previewMessage.value = `✓ ${response.data.message}`
      previewMessageClass.value = 'preview-success'

      setTimeout(() => {
        previewMessage.value = ''
        previewMessageClass.value = ''
      }, 5000) // Show success message longer (5 seconds)
    } else {
      previewMessage.value = '✗ Preview send failed'
      previewMessageClass.value = 'preview-error'
      console.error('Preview send failed:', response.data.error)
    }
  } catch (error) {
    previewMessage.value = '✗ Error sending preview'
    previewMessageClass.value = 'preview-error'
    const errorMsg = error.response?.data?.error || error.message
    console.error('Preview send error:', errorMsg)

    setTimeout(() => {
      previewMessage.value = ''
      previewMessageClass.value = ''
    }, 3000)
  } finally {
    previewLoading.value = false
  }
}

// Expose methods to parent
defineExpose({
  validate,
  getAllVariants,
  saveCurrentVariant,
  sendPreview,
  getEmailContentId: () => emailContentIdStore.value,
  canSendPreview,
  previewLoading,
  previewMessage,
  previewMessageClass
})
</script>

<style>
/* Trix editor list formatting improvements */
:deep(trix-editor) ul,
:deep(trix-editor) ol {
  padding-left: 30px !important;
  margin-left: 10px !important;
  margin-top: 10px;
  margin-bottom: 10px;
}

:deep(trix-editor) li {
  margin-bottom: 6px;
  padding-left: 5px;
}

:deep(trix-editor) ul li {
  list-style-type: disc;
}

:deep(trix-editor) ol li {
  list-style-type: decimal;
}

:deep(trix-editor) p {
  margin-bottom: 10px;
}

:deep(trix-editor) ul ul,
:deep(trix-editor) ol ol,
:deep(trix-editor) ul ol,
:deep(trix-editor) ol ul {
  padding-left: 25px !important;
  margin-top: 5px;
  margin-bottom: 5px;
}
</style>
